import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cart } from "../../types";

type AdminDiscountInput = {
  type: "percentage" | "fixed_amount";
  value: number;
  reason: string;
};

type Props = {
  onApply: (input: AdminDiscountInput) => Promise<Cart>;
  onClear?: () => Promise<Cart>;
  onCartUpdate: (cart: Cart) => void;
};

export function AdminDiscountControls({
  onApply,
  onClear,
  onCartUpdate,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [applied, setApplied] = useState<AdminDiscountInput | null>(null);
  const [type, setType] = useState<AdminDiscountInput["type"]>("percentage");
  const [value, setValue] = useState<string>("10");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (!reason.trim()) {
      setError("Discount reason is required.");
      return;
    }
    const parsedVal = parseFloat(value) || 0;
    if (parsedVal <= 0) {
      setError("Discount amount or percentage must be greater than 0.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = { type, value: parsedVal, reason: reason.trim() };
      const updated = await onApply(next);
      onCartUpdate(updated);
      setApplied(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply discount");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!onClear) return;
    setBusy(true);
    setError(null);
    try {
      onCartUpdate(await onClear());
      setApplied(null);
      setExpanded(false);
      setValue("10");
      setReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not clear discount");
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    const display =
      applied.type === "percentage"
        ? `${applied.value}%`
        : `$${applied.value.toFixed(2)}`;
    return (
      <div
        className="flex items-center gap-2 text-sm py-2"
        data-testid="admin-discount-controls"
      >
        <Check className="h-4 w-4 text-purple-500 flex-shrink-0" />
        <span className="text-purple-700 dark:text-purple-300 font-medium">
          Admin discount &mdash; {display}
        </span>
        {onClear ? (
          <button
            className="ml-auto text-sm text-muted-foreground underline hover:no-underline"
            disabled={busy}
            onClick={clear}
            type="button"
          >
            Remove
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div data-testid="admin-discount-controls">
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Apply admin discount
      </button>

      {expanded ? (
        <div className="mt-3 pl-5 space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-1.5 dark:text-gray-200">
              <input
                aria-label="Admin discount type"
                checked={type === "percentage"}
                name="admin-discount-type"
                onChange={() => setType("percentage")}
                type="radio"
                value="percentage"
              />
              Percentage
            </label>
            <label className="flex items-center gap-1.5 dark:text-gray-200">
              <input
                checked={type === "fixed_amount"}
                name="admin-discount-type"
                onChange={() => setType("fixed_amount")}
                type="radio"
                value="fixed_amount"
              />
              Fixed Amount
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="admin-discount-value">
                {type === "percentage" ? "Percentage" : "Amount (per seat)"}
              </Label>
              <Input
                aria-label="Admin discount value"
                className="h-9"
                disabled={busy}
                id="admin-discount-value"
                onChange={(e) => setValue(e.target.value)}
                placeholder={type === "percentage" ? "10" : "25.00"}
                type="number"
                value={value}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="admin-discount-reason">
                Reason
              </Label>
              <Input
                aria-label="Admin discount reason"
                className="h-9"
                disabled={busy}
                id="admin-discount-reason"
                onChange={(e) => setReason(e.target.value)}
                placeholder="Required"
                type="text"
                value={reason}
              />
            </div>
          </div>

          {error ? (
            <p
              className="text-xs text-destructive"
              data-testid="admin-discount-error"
            >
              {error}
            </p>
          ) : null}

          <Button
            disabled={busy}
            onClick={apply}
            size="sm"
            type="button"
            variant="outline"
          >
            {busy ? "Applying…" : "Apply discount"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
